import { getSymbolFromCode } from "currency-code-symbol-map";

/**
 * Formatting helpers for the Player Insights tab.
 *
 * Brief rules encoded here (§2.6): large values abbreviated (2.02M / 23.8K),
 * tabular numerals, minus sign for negatives and never parentheses. Money
 * language stays clinical — GGR, hold, turnover, stake.
 *
 * Money carries no fixed currency. The brief's "KES everywhere" described its
 * Kenyan sample player; the platform runs brands in USDT, KES, USD and more,
 * so the formatters are built per profile from the currency UPS reports in
 * (see makeMoney and ./currency.js). Amounts arrive as major units, already
 * converted by UPS — nothing here divides by 100.
 */

/** 2,016,000 -> "2.02M" · 23,800 -> "23.8K" · 5,000 -> "5,000" */
export const abbreviateNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);

  // A B tier so a bad upstream figure prints "2.1B", never sixteen digits.
  if (abs >= 1_000_000_000) return `${sign}${trimZeros(abs / 1_000_000_000, 2)}B`;
  if (abs >= 1_000_000) return `${sign}${trimZeros(abs / 1_000_000, 2)}M`;
  if (abs >= 10_000) return `${sign}${trimZeros(abs / 1_000, 1)}K`;
  return `${sign}${abs.toLocaleString("en-US")}`;
};

/**
 * How the rest of the back office labels money: the fiat symbol from the same
 * library Currency() in sharedfiles/helper.js uses, and the bare code when the
 * library has none — which is every crypto currency. Deliberately not the
 * ₮ / ₿ glyphs some newer screens use: operators read "USDT", not "₮".
 */
export const currencySymbol = (code) => {
  if (!code) return "";
  const upper = String(code).toUpperCase();
  return getSymbolFromCode(upper) || upper;
};

/** "USDT 2.02M" but "$2.02M" — a space only after a lettered symbol. */
const joinSymbol = (symbol, text) => {
  if (!symbol) return text;
  return /^[A-Za-z]/.test(symbol) ? `${symbol} ${text}` : `${symbol}${text}`;
};

const MAX_PRECISION = 10;

/**
 * Money formatters for one currency.
 *
 *   amount(v)      "5,000" / "0.00015"   — number only, up to `precision` places
 *   money(v)       "USDT 2.02M"          — abbreviated above 10K, for big figures
 *   moneyExact(v)  "USDT 2,016,000"      — never abbreviated, for tooltips
 *
 * `precision` is the currency's own (2 for USDT/KES, 10 for BTC/ETH), so a
 * 0.00015 BTC stake is not rounded away to "0".
 */
export const makeMoney = (code, precision = 2) => {
  const symbol = currencySymbol(code);
  const places = Math.min(Math.max(Number.isFinite(precision) ? precision : 2, 0), MAX_PRECISION);

  const amount = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    return Math.abs(value).toLocaleString("en-US", { maximumFractionDigits: places });
  };

  const moneyExact = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    const sign = value < 0 ? "-" : "";
    return joinSymbol(symbol, `${sign}${amount(value)}`);
  };

  const money = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    if (Math.abs(value) >= 10_000) return joinSymbol(symbol, abbreviateNumber(value));
    return moneyExact(value);
  };

  return { code: code ? String(code).toUpperCase() : "", symbol, precision: places, amount, money, moneyExact };
};

/** Plain counts: 20160 -> "20,160" */
export const count = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Math.round(value).toLocaleString("en-US");
};

/** Counts abbreviated for sub-lines: 16800 -> "16.8K" */
export const countShort = (value) => abbreviateNumber(value);

/** 14.166 -> "14.2%". Sub-1% shares keep a decimal so 0.8% never reads as 1%. */
export const pct = (value, decimals) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  // A hold of ten thousand percent is a data problem, not a figure to typeset in full.
  if (Math.abs(value) >= 10_000) return `${abbreviateNumber(value)}%`;
  const places = decimals !== undefined ? decimals : Math.abs(value) < 1 ? 1 : value % 1 === 0 ? 0 : 1;
  return `${trimZeros(value, places)}%`;
};

/** "2026-04-20T18:42:00Z" -> "20 Apr 18:42" */
export const dayTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

/** "2026-04-21" -> "21 Apr" */
export const dayMonth = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/** "2026-04" -> "Apr 26" for a dense axis, "April 2026" when spelled out. */
export const monthShort = (key) => {
  const [year, month] = String(key).split("-");
  return `${MONTHS[Number(month) - 1]} ${year.slice(2)}`;
};

export const monthLong = (key) => {
  const [year, month] = String(key).split("-");
  return `${MONTH_NAMES[Number(month) - 1]} ${year}`;
};

/** Whole days between an ISO timestamp and `now`. */
export const daysSince = (iso, now = new Date()) => {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((now.getTime() - then.getTime()) / 86_400_000);
};

/** snake_case tokens from the JSON rendered as readable labels. */
export const humanise = (token) =>
  String(token)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/** Bet-type keys are terser than plain humanise can handle. */
export const BET_TYPE_LABELS = {
  single: "Single",
  double: "Double",
  "3fold": "3-fold",
  "4to5fold": "4–5 fold",
  "6to10fold": "6–10 fold",
  "10fold_plus": "10-fold+",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");

/** 2.016 -> "2.02" but 5.0 -> "5" — avoids trailing-zero noise in big figures. */
function trimZeros(value, places) {
  const fixed = value.toFixed(places);
  return fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
}
